/**
 * On-device speech-to-text using Whisper tiny.en via @huggingface/transformers.
 * Runs in single-threaded WASM mode - no COOP/COEP headers needed.
 */

import type { VoiceTranscription, ModelLoadingProgress } from './types';

let pipeline: any = null;

export async function loadWhisperModel(
  onProgress?: (progress: ModelLoadingProgress) => void,
): Promise<void> {
  if (pipeline) return;

  onProgress?.({ model: 'whisper', loaded: false, progress: 0 });

  try {
    const { pipeline: createPipeline, env } = await import('@huggingface/transformers');

    // Force single-threaded WASM (no SharedArrayBuffer / COOP/COEP needed)
    env.backends.onnx.wasm.numThreads = 1;

    onProgress?.({ model: 'whisper', loaded: false, progress: 30 });

    pipeline = await createPipeline(
      'automatic-speech-recognition',
      'onnx-community/whisper-tiny.en',
      {
        dtype: 'q8',
        device: 'wasm',
      },
    );

    onProgress?.({ model: 'whisper', loaded: true, progress: 100 });
    console.log('[WhisperSTT] Model loaded');
  } catch (error) {
    pipeline = null;
    onProgress?.({
      model: 'whisper',
      loaded: false,
      progress: 0,
      error: String(error),
    });
    throw error;
  }
}

export async function transcribe(audioData: Float32Array): Promise<VoiceTranscription> {
  if (!pipeline) throw new Error('Whisper model not loaded');

  const result = await pipeline(audioData, {
    return_timestamps: false,
  });

  // Strip Whisper special tokens like [BLANK_AUDIO], (music), etc.
  let text = (result.text || '').trim();
  text = text.replace(/\[BLANK_AUDIO\]/gi, '').replace(/\(music\)/gi, '').trim();
  console.log('[WhisperSTT] Transcription:', text);

  return {
    text,
    confidence: 1.0,
    language: 'en',
  };
}

export function destroyWhisperModel(): void {
  pipeline = null;
  console.log('[WhisperSTT] Model destroyed');
}

export function isWhisperLoaded(): boolean {
  return pipeline !== null;
}
